import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields, getAllFields, AGGREGATION_TYPES } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

const TREEMAP_COLORS = [
  { name: 'Bosque', colors: ['#10b981', '#059669', '#047857', '#065f46'] },
  { name: 'Océano', colors: ['#3b82f6', '#2563eb', '#1d4ed8', '#1e40af'] },
  { name: 'Atardecer', colors: ['#f59e0b', '#f97316', '#ef4444', '#dc2626'] },
]

export function TreemapConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  const { aggregatableFields, allFields } = useMemo(() => {
    if (!config.dataSource) return { aggregatableFields: [], allFields: [] }

    if (
      config.dataSource === 'custom_query' &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      const fieldsArray = fieldsFromCustomQuery(config.customQuery)
      return {
        aggregatableFields: fieldsArray.filter((f) => f.aggregatable),
        allFields: fieldsArray,
      }
    }

    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return {
        aggregatableFields: dynamicSource.fields.filter(f => f.aggregatable),
        allFields: dynamicSource.fields
      }
    }

    return {
      aggregatableFields: getAggregatableFields(config.dataSource),
      allFields: getAllFields(config.dataSource)
    }
  }, [dataSources, config.dataSource, cqSig])

  const categoryFields = allFields.filter(f => f.type === 'string' || !f.aggregatable)

  const treemapConfig = config.treemapConfig || { nameKey: '', valueKey: '' }

  const isValid = Boolean(
    config.metadata?.title &&
    treemapConfig.nameKey &&
    treemapConfig.valueKey &&
    config.aggregation
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Configurar Mapa de Árbol</h2>
      <p className="text-gray-600 mb-6">
        Proporciones jerárquicas con rectángulos anidados
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título del Componente <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Distribución de Formaciones Forestales"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Campo de Categorías (nameKey) */}
        <div>
          <label className="block font-medium mb-2">
            Campo de Categorías <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={treemapConfig.nameKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                treemapConfig: { ...treemapConfig, nameKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {categoryFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Nombre de las categorías (ej: especies, formaciones)
          </p>
        </div>

        {/* Campo Numérico (valueKey) */}
        <div>
          <label className="block font-medium mb-2">
            Campo de Valores <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={treemapConfig.valueKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                treemapConfig: { ...treemapConfig, valueKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Campo numérico que determina el tamaño de cada rectángulo
          </p>
        </div>

        {/* Agregación */}
        <div>
          <label className="block font-medium mb-2">
            Operación <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-5 gap-3">
            {AGGREGATION_TYPES.map((agg) => (
              <button
                key={agg.value}
                type="button"
                className={`
                  border-2 rounded p-3 text-center transition-all
                  ${
                    config.aggregation === agg.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() => onChange({ ...config, aggregation: agg.value as any })}
              >
                <div className="text-xl mb-1">{agg.icon}</div>
                <div className="text-xs font-medium">{agg.value}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Paleta de colores */}
        <div>
          <label className="block font-medium mb-2">Paleta de Colores</label>
          <div className="grid grid-cols-3 gap-3">
            {TREEMAP_COLORS.map((palette) => (
              <button
                key={palette.name}
                type="button"
                className={`
                  border-2 rounded p-3 transition-all
                  ${
                    JSON.stringify(treemapConfig.colors) === JSON.stringify(palette.colors)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    treemapConfig: { ...treemapConfig, colors: palette.colors },
                  })
                }
              >
                <div className="flex gap-1 mb-2">
                  {palette.colors.map((color, i) => (
                    <div key={i} className="w-full h-8 rounded" style={{ backgroundColor: color }}></div>
                  ))}
                </div>
                <div className="text-sm font-medium">{palette.name}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}
